import { NgClass, NgFor, NgIf, NgStyle } from '@angular/common';
import { ChangeDetectorRef, Component, DestroyRef, EventEmitter, Input, OnChanges, OnInit, Output, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

import { TimerService } from '@frontend/vanilla/core';
import { Subject, Subscription } from 'rxjs';

import { InstaPromoModel } from '../shared/model/insta-promo.model';
import { StoryModel } from '../shared/model/story.model';
import { UserActionService } from '../shared/services/user-action.service';

@Component({
    selector: 'cc-instagram-story-progress-bar',
    templateUrl: 'instagram-story-progress-bar.component.html',
    imports: [NgFor, NgStyle, NgIf, NgClass],
})
export class InstagramStoryProgressBarComponent implements OnInit, OnChanges {
    @Output() sendStoryCompletionEvent: EventEmitter<any> = new EventEmitter<any>();
    @Input() activePromoIndex: number;
    @Input() activePromoStoryIndex: number;
    @Input() storyData: StoryModel;
    width: number = 0;
    progressBarWidth: string;
    progressBarBgClr: string;
    progressBarClr: string;
    progressBarCompletionBgClr: string;
    progressBarCompletionClr: string;
    activePromo: InstaPromoModel;
    pauseStoryObservable: Subscription;
    pauseClick = new Subject();
    storyCompletionArray: Array<any> = new Array<any>();
    changeStoryObservable: Subscription;
    setIntervalTimer: any;
    play: boolean;
    progress = 0;
    storyInterval: number;
    videoPresentObservable: Subscription;
    videoProgressObservable: Subscription;
    private destroyRef = inject(DestroyRef);

    constructor(
        private userActionService: UserActionService,
        private timerService: TimerService,
        private changeDetectorRef: ChangeDetectorRef,
    ) {}

    ngOnInit() {
        this.progressBarBgClr = this.storyData.sharedList.progressBarBgClr;
        this.progressBarClr = this.storyData.sharedList.progressBarClr;
        this.progressBarCompletionBgClr = this.storyData.sharedList.progressBarCompletionBgClr;
        this.progressBarCompletionClr = this.storyData.sharedList.progressBarCompletionClr;
        this.pauseStoryObservable = this.userActionService.pauseStoryObservable
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe((pauseStoryTrigger: boolean) => {
                if (pauseStoryTrigger) {
                    this.pauseStory();
                } else {
                    this.resumeStory();
                }
            });
    }

    ngOnChanges() {
        clearTimeout(this.setIntervalTimer);
        if (this.setIntervalTimer) {
            this.timerService.clearInterval(this.setIntervalTimer);
        }
        this.setIntervalTimer = 0;
        this.progress = 0;
        this.play = true;
        this.width = 0;
        this.storyCompletionArray = [];
        this.activePromo = this.storyData.instaPromoList[this.activePromoIndex];
        this.progressBarWidth = (100 / this.activePromo.instaStoryContentModel.length).toString() + '%';
        let i = this.activePromoStoryIndex - 1;
        this.storyInterval = this.storyData.storyDisplayInterval ? this.storyData.storyDisplayInterval : 10;
        while (i >= 0) {
            this.storyCompletionArray.push(i);
            i--;
        }
        this.subscribeToVideoPresentObs();
        this.runProgressBar();
    }

    subscribeToVideoPresentObs() {
        this.videoPresentObservable = this.userActionService.videoPresentObservable
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe((isVideoPresent: boolean) => {
                if (isVideoPresent) {
                    this.clearTimer();
                    this.progress = 0;
                    this.width = 0;
                }
            });

        this.videoProgressObservable = this.userActionService.videoProgressObservable
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe((progress: number) => {
                if (this.play && progress != -1) {
                    this.width = progress;
                    if (this.width == 100) {
                        this.emitStoryCompletionEvent();
                    }
                }
            });
    }

    unsubscribeToVideoObservable() {
        if (this.videoPresentObservable) {
            this.videoPresentObservable.unsubscribe();
        }
        if (this.videoProgressObservable) {
            this.videoProgressObservable.unsubscribe();
        }
    }

    runProgressBar() {
        this.setIntervalTimer = this.timerService.setIntervalOutsideAngularZone(() => {
            if (this.play) {
                this.progress += 0.001;
                this.width = Math.floor(this.progress * 100);
                this.changeDetectorRef.detectChanges();
                if (this.progress >= 1) {
                    this.emitStoryCompletionEvent();
                }
            }
        }, this.storyInterval);
    }

    getWidth() {
        return this.width + '%';
    }

    emitStoryCompletionEvent() {
        const activePromoData = {
            activePromoIndex: this.activePromoIndex,
            activePromoStoryIndex: this.activePromoStoryIndex,
        };
        this.clearTimer();
        this.unsubscribeToVideoObservable();
        this.userActionService.videoPresentStatus(false);
        this.userActionService.videoProgressStatus(-1);
        this.sendStoryCompletionEvent.emit(activePromoData);
    }

    clearTimer() {
        clearTimeout(this.setIntervalTimer);
        if (this.setIntervalTimer) {
            this.timerService.clearInterval(this.setIntervalTimer);
            this.setIntervalTimer = undefined;
        }
    }

    pauseStory() {
        this.userActionService.pauseVideo();
        this.play = false;
        const video = <HTMLVideoElement>document.getElementById('casino-promoinstastory-video');
        if (video) {
            video.pause();
        }
    }

    resumeStory() {
        this.userActionService.playVideo();
        this.play = true;
    }

    ngOnDestroy() {
        this.storyCompletionArray = [];
        if (this.pauseStoryObservable) {
            this.pauseStoryObservable.unsubscribe();
        }
        this.clearTimer();
    }

    checkStoryCompletion(index: number) {
        return this.storyCompletionArray.indexOf(index) > -1;
    }
}
